import { NotifyListDisplayTypes, NotifyListSections } from 'constants/service/notifyList';
import { NotifyListItem, NotifyListItemHeader, NotifyListItemParty } from 'types/service';
import { StoreType } from '@tock/shared/utils/connect';
import { createSelector } from 'reselect';
import { dashboard } from 'types';
import {
  filterByGuestTags,
  filterBySelectedPartyListExperiences,
  filterBySelectedPartyListServices,
  filterBySelectedPartyListSizes,
  getWaitlistFilterValues,
} from 'utils/service/partyListUtils';
import {
  getPartyListTextFilterSelector,
  selectedPartyListFilterValuesSelector,
} from './partyListSelector';
import Fuse from 'fuse.js';
import _ from 'lodash';

const REQUESTED_HEADER: NotifyListItemHeader = {
  listItemType: 'waitlistHeader',
  displayType: NotifyListDisplayTypes.HEADER,
  contentType: NotifyListSections.REQUESTED,
  label: 'Active',
  coverCount: 0,
  partyCount: 0,
};

const NOTIFIED_HEADER: NotifyListItemHeader = {
  listItemType: 'waitlistHeader',
  displayType: NotifyListDisplayTypes.HEADER,
  contentType: NotifyListSections.NOTIFIED,
  label: 'Notified',
  coverCount: 0,
  partyCount: 0,
};

const FULFILLED_HEADER: NotifyListItemHeader = {
  listItemType: 'waitlistHeader',
  displayType: NotifyListDisplayTypes.HEADER,
  contentType: NotifyListSections.FULFILLED,
  label: 'Fulfilled',
  coverCount: 0,
  partyCount: 0,
};

const queryWaitlist = (waitlistParties: dashboard.CalendarWaitlist[], query: string) => {
  if (!query) {
    return waitlistParties;
  }

  const fuzzySearch = new Fuse(waitlistParties, {
    includeScore: false,
    includeMatches: false,
    keys: [
      'guestProfile.patron.firstName',
      'guestProfile.patron.lastName',
      'guestProfile.patron.email',
      'guestProfile.patron.phone',
    ],
    threshold: 0.4,
  });

  return _.map(fuzzySearch.search(query), (result) => result.item);
};

const filteredWaitlists = createSelector(
  (state: StoreType) => state.calendar.waitlists,
  getPartyListTextFilterSelector,
  selectedPartyListFilterValuesSelector,
  (waitlists, query, filters) => {
    const {
      selectedPartyListServices,
      selectedPartyListExperiences,
      selectedPartyListSizes,
      selectedPartyListStatus,
      selectedPartyListGuestTags,
      selectedPartyListVisitTags,
      selectedPartyListSupplements,
      selectedPartyListFilterUnanswered,
    } = filters;

    // if any unsupported filters have been applied return no matches
    if (
      selectedPartyListStatus.length ||
      selectedPartyListSupplements.length ||
      selectedPartyListVisitTags.length ||
      selectedPartyListFilterUnanswered.showUnansweredQuestions
    ) {
      return [];
    }

    const queriedParties = queryWaitlist(Object.values(waitlists), query);

    return queriedParties.filter((waitlistParty: dashboard.CalendarWaitlist) => {
      const { experienceId, experienceName, guestProfile, partySize, serviceType } =
        getWaitlistFilterValues(waitlistParty);

      return (
        filterByGuestTags(selectedPartyListGuestTags, guestProfile) &&
        filterBySelectedPartyListServices(selectedPartyListServices, serviceType) &&
        filterBySelectedPartyListSizes(selectedPartyListSizes, partySize) &&
        filterBySelectedPartyListExperiences(selectedPartyListExperiences, {
          experienceName,
          experienceId,
        })
      );
    });
  }
);

export const singleNotifyListSelector = createSelector(
  filteredWaitlists,
  (waitlists: dashboard.CalendarWaitlist[]): NotifyListItem[] => {
    if (_.isEmpty(waitlists)) {
      return [REQUESTED_HEADER, NOTIFIED_HEADER, FULFILLED_HEADER];
    }

    const notifyList = _.sortBy(waitlists, ['desiredSize', 'lastName', 'firstName']);

    const activeItems: NotifyListItemParty[] = [];
    const notifiedItems: NotifyListItemParty[] = [];
    const fulfilledItems: NotifyListItemParty[] = [];

    notifyList.forEach((item) => {
      switch (item.state) {
        case 'ACTIVE':
          activeItems.push({
            displayType: NotifyListDisplayTypes.PARTY,
            listItemType: 'waitlistParty',
            item,
          });
          break;
        case 'NOTIFIED':
          notifiedItems.push({
            displayType: NotifyListDisplayTypes.PARTY,
            listItemType: 'waitlistParty',
            item,
          });
          break;
        case 'FULFILLED':
          fulfilledItems.push({
            displayType: NotifyListDisplayTypes.PARTY,
            listItemType: 'waitlistParty',
            item,
          });
          break;
      }
    });

    return [
      ...addHeader(activeItems, REQUESTED_HEADER),
      ...addHeader(notifiedItems, NOTIFIED_HEADER),
      ...addHeader(fulfilledItems, FULFILLED_HEADER),
    ];
  }
);

/**
 * Summarizes the party and covers within a section.
 * @returns array with the list items and header item
 */
function addHeader(
  listItems: NotifyListItemParty[],
  header: NotifyListItemHeader
): NotifyListItem[] {
  return [
    {
      ...header,
      coverCount: listItems.reduce((sum, { item }) => sum + item.desiredSize, 0),
      partyCount: listItems.length,
    },
    ...listItems,
  ];
}

export const selectedNotifyListItem = createSelector(
  (state: StoreType) => state.service.ui.selectedWaitlistId,
  (state: StoreType) => state.calendar.waitlists,
  (waitlistId, waitlists): dashboard.CalendarWaitlist | undefined => {
    if (!waitlistId) {
      return;
    }

    return waitlists[waitlistId];
  }
);
